'use client'

import type { Theme } from '@/providers/Theme/types'

import React, { createContext, useCallback, use, useState } from 'react'

type HeaderVariant = StandardVariant | MinimalVariant | ThinWithTitleVariant | NoNavVariant

interface NoNavVariant {
    theme: Theme
    variant: 'nonav'
}

interface StandardVariant {
    theme: Theme
    variant: 'standard'
}

interface MinimalVariant {
    theme: Theme
    variant: 'minimal'
}

interface ThinWithTitleVariant {
    theme: Theme
    variant: 'thin'
    title: string
}

export interface ContextType {
    headerVariant: HeaderVariant
    setHeaderVariant: (variantToSet: HeaderVariant) => void
}

const initialContext: ContextType = {
    headerVariant: { theme: 'light', variant: 'standard' },
    setHeaderVariant: () => null,
}

const HeaderVariantContext = createContext(initialContext)

export const HeaderVariantProvider = ({ children }: { children: React.ReactNode }) => {
    // We cannot read the initial state from DOM because header variants are updated
    // on the client only due to useEffect. In the future, we can consider a method
    // where we can update the server side DOM based on page rendered
    const [headerVariant, setHeaderVariantState] = useState<HeaderVariant>(initialContext.headerVariant)

    const setHeaderVariant = useCallback((variantToSet: HeaderVariant) => {
        setHeaderVariantState(variantToSet)
    }, [])

    return <HeaderVariantContext value={{ headerVariant, setHeaderVariant }}>{children}</HeaderVariantContext>
}

export const useHeaderVariant = (): ContextType => use(HeaderVariantContext)
